import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import '../styles/Community.css';

interface Category {
  id: string;
  name: string;
  displayName: string;
  description: string;
  icon: string;
  discussionCount: number;
}

interface Discussion {
  id: string;
  title: string;
  content: string;
  viewCount: number;
  likeCount: number;
  replyCount: number;
  isPinned: boolean;
  createdAt: string;
  author: { id: string; fullName: string };
  category: { name: string; displayName: string; icon: string };
  _count: { replies: number };
}

interface Reply {
  id: string;
  content: string;
  likeCount: number;
  createdAt: string;
  author: { id: string; fullName: string };
  childReplies?: Reply[];
}

interface DiscussionDetail extends Discussion {
  author: { id: string; fullName: string };
  replies: Reply[];
}

const Community: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedDiscussion, setSelectedDiscussion] = useState<DiscussionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'categories' | 'discussions' | 'discussion'>('categories');
  const [sortBy, setSortBy] = useState('latest');
  const [searchQuery, setSearchQuery] = useState('');
  
  // New discussion form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState('');
  
  // Reply form
  const [replyContent, setReplyContent] = useState('');
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [replyToName, setReplyToName] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  // Current user for ownership checks
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Edit state
  const [editingDiscussionId, setEditingDiscussionId] = useState<string | null>(null);
  const [editDiscTitle, setEditDiscTitle] = useState('');
  const [editDiscContent, setEditDiscContent] = useState('');
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editReplyContent, setEditReplyContent] = useState('');
  
  const navigate = useNavigate();

  // Fetch current user id
  useEffect(() => {
    const fetchMe = async () => {
      try {
        const res = await axiosInstance.get('/auth/profile');
        setCurrentUserId(res.data.user?.id || null);
      } catch { /* ignore */ }
    };
    fetchMe();
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/auth/community/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  const fetchDiscussions = useCallback(async (category?: string | null) => {
    try {
      setLoading(true);
      const params: any = { sort: sortBy };
      if (category) params.category = category;
      if (searchQuery) params.search = searchQuery;
      
      const response = await axiosInstance.get('/auth/community/discussions', { params });
      setDiscussions(response.data.discussions || []);
    } catch (error) {
      console.error('Failed to fetch discussions:', error);
    } finally {
      setLoading(false);
    }
  }, [sortBy, searchQuery]);

  const fetchDiscussion = async (id: string) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/auth/community/discussions/${id}`);
      setSelectedDiscussion(response.data.discussion);
      setView('discussion');
    } catch (error) {
      console.error('Failed to fetch discussion:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchDiscussions();
  }, [fetchCategories, fetchDiscussions]);

  const handleCategoryClick = (categoryName: string) => {
    setSelectedCategory(categoryName);
    setView('discussions');
    fetchDiscussions(categoryName);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setView('categories');
    fetchDiscussions();
  };

  const handleBackToDiscussions = () => {
    setSelectedDiscussion(null);
    setView('discussions');
  };

  const handleCreateDiscussion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategory) { toast.error('Please choose a category'); return; }
    if (newTitle.trim().length < 3) { toast.error('Title must be at least 3 characters'); return; }
    if (newTitle.trim().length > 200) { toast.error('Title is too long (max 200 characters)'); return; }
    if (!newContent.trim()) { toast.error('Content is required'); return; }
    if (newContent.length > 10000) { toast.error('Content is too long (max 10,000 characters)'); return; }

    try {
      setSubmitting(true);
      await axiosInstance.post('/auth/community/discussions', {
        categoryName: newCategory,
        title: newTitle,
        content: newContent
      });
      
      setShowNewForm(false);
      setNewTitle('');
      setNewContent('');
      setNewCategory('');
      fetchDiscussions(selectedCategory);
    } catch (error) {
      console.error('Failed to create discussion:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim() || !selectedDiscussion) return;
    if (replyContent.length > 10000) { toast.error('Reply is too long (max 10,000 characters)'); return; }

    try {
      setSubmitting(true);
      await axiosInstance.post(`/auth/community/discussions/${selectedDiscussion.id}/reply`, {
        content: replyContent,
        parentReplyId: replyToId || undefined,
      });
      
      setReplyContent('');
      setReplyToId(null);
      setReplyToName('');
      fetchDiscussion(selectedDiscussion.id);
      toast.success('Reply posted!');
    } catch (error) {
      console.error('Failed to post reply:', error);
      toast.error('Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplyLike = async (replyId: string) => {
    try {
      await axiosInstance.post(`/auth/community/replies/${replyId}/like`);
      if (selectedDiscussion) fetchDiscussion(selectedDiscussion.id);
    } catch (error) {
      console.error('Failed to like reply:', error);
    }
  };

  const handleDeleteDiscussion = async (discId: string) => {
    if (!window.confirm('Delete this discussion? This cannot be undone.')) return;
    try {
      await axiosInstance.delete(`/auth/community/discussions/${discId}`);
      toast.success('Discussion deleted');
      handleBackToDiscussions();
      fetchDiscussions(selectedCategory);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete');
    }
  };

  const handleEditDiscussion = async () => {
    if (!editingDiscussionId || !editDiscTitle.trim() || !editDiscContent.trim()) return;
    if (editDiscTitle.trim().length < 3) { toast.error('Title must be at least 3 characters'); return; }
    if (editDiscTitle.trim().length > 200 || editDiscContent.length > 10000) { toast.error('Title or content is too long'); return; }
    try {
      await axiosInstance.put(`/auth/community/discussions/${editingDiscussionId}`, {
        title: editDiscTitle,
        content: editDiscContent,
      });
      toast.success('Discussion updated');
      setEditingDiscussionId(null);
      if (selectedDiscussion) fetchDiscussion(selectedDiscussion.id);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update');
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await axiosInstance.delete(`/auth/community/replies/${replyId}`);
      toast.success('Reply deleted');
      if (selectedDiscussion) fetchDiscussion(selectedDiscussion.id);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete');
    }
  };

  const handleEditReply = async () => {
    if (!editingReplyId || !editReplyContent.trim()) return;
    if (editReplyContent.length > 10000) { toast.error('Reply is too long (max 10,000 characters)'); return; }
    try {
      await axiosInstance.put(`/auth/community/replies/${editingReplyId}`, {
        content: editReplyContent,
      });
      toast.success('Reply updated');
      setEditingReplyId(null);
      if (selectedDiscussion) fetchDiscussion(selectedDiscussion.id);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update');
    }
  };

  const startReplyTo = (replyId: string, authorName: string) => {
    setReplyToId(replyId);
    setReplyToName(authorName);
  };

  const handleLike = async (discussionId: string) => {
    try {
      await axiosInstance.post(`/auth/community/discussions/${discussionId}/like`);
      if (selectedDiscussion?.id === discussionId) {
        fetchDiscussion(discussionId);
      } else {
        fetchDiscussions(selectedCategory);
      }
    } catch (error) {
      console.error('Failed to like:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const mins = Math.floor(diff / (1000 * 60));
        return mins <= 1 ? 'Just now' : `${mins}m ago`;
      }
      return `${hours}h ago`;
    }
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="comm-container">
      <div className="comm-header">
        <button className="comm-back-btn" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <div className="comm-header-content">
          <h1>Community Hub</h1>
          <p>Connect, share, and learn from fellow job seekers</p>
        </div>
        <div className="comm-header-actions">
          <button 
            className="comm-new-btn" 
            onClick={() => setShowNewForm(true)}
          >
            + New Discussion
          </button>
          <button 
            className="comm-profile-btn"
            onClick={() => navigate('/community/profile')}
          >
            My Profile
          </button>
          <button 
            className="comm-members-btn"
            onClick={() => navigate('/community/members')}
          >
            👥 Members
          </button>
          <button 
            className="comm-connections-btn"
            onClick={() => navigate('/community/connections')}
          >
            🤝 Connections
          </button>
        </div>
      </div>

      {/* New Discussion Modal */}
      {showNewForm && (
        <div className="comm-modal-overlay" onClick={() => setShowNewForm(false)}>
          <div className="comm-modal" onClick={e => e.stopPropagation()}>
            <h2>Start a Discussion</h2>
            <form onSubmit={handleCreateDiscussion}>
              <div className="comm-form-group">
                <label>Category</label>
                <select 
                  value={newCategory} 
                  onChange={e => setNewCategory(e.target.value)}
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>
                      {cat.icon} {cat.displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="comm-form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                  placeholder="What's your question or topic?"
                  required
                />
              </div>
              <div className="comm-form-group">
                <label>Content</label>
                <textarea
                  value={newContent}
                  onChange={e => setNewContent(e.target.value)}
                  placeholder="Share more details..."
                  rows={5}
                  required
                />
              </div>
              <div className="comm-modal-actions">
                <button type="button" onClick={() => setShowNewForm(false)}>Cancel</button>
                <button type="submit" disabled={submitting}>
                  {submitting ? 'Posting...' : 'Post Discussion'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Categories View */}
      {view === 'categories' && (
        <div className="comm-categories">
          <div className="comm-search-bar">
            <input
              type="text"
              placeholder="Search discussions..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchDiscussions()}
            />
            <button onClick={() => fetchDiscussions()}>Search</button>
          </div>

          <h2>Browse by Category</h2>
          <div className="comm-categories-grid">
            {categories.map(cat => (
              <div 
                key={cat.id} 
                className="comm-category-card"
                onClick={() => handleCategoryClick(cat.name)}
              >
                <span className="comm-category-icon">{cat.icon}</span>
                <h3>{cat.displayName}</h3>
                <p>{cat.description}</p>
                <span className="comm-category-count">
                  {cat.discussionCount} discussion{cat.discussionCount !== 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>

          <h2>Recent Discussions</h2>
          <div className="comm-discussions-list">
            {discussions.slice(0, 5).map(disc => (
              <div 
                key={disc.id} 
                className="comm-discussion-item"
                onClick={() => fetchDiscussion(disc.id)}
              >
                <div className="comm-discussion-meta">
                  <span className="comm-discussion-category">
                    {disc.category.icon} {disc.category.displayName}
                  </span>
                  {disc.isPinned && <span className="comm-pinned">📌 Pinned</span>}
                </div>
                <h3>{disc.title}</h3>
                <p>{disc.content.substring(0, 150)}{disc.content.length > 150 ? '...' : ''}</p>
                <div className="comm-discussion-stats">
                  <span>👤 {disc.author.fullName}</span>
                  <span>👁 {disc.viewCount}</span>
                  <span>❤️ {disc.likeCount}</span>
                  <span>💬 {disc._count.replies}</span>
                  <span>{formatDate(disc.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Discussions List View */}
      {view === 'discussions' && (
        <div className="comm-discussions">
          <button className="comm-breadcrumb" onClick={handleBackToCategories}>
            ← All Categories
          </button>
          
          {selectedCategory && (
            <h2>
              {categories.find(c => c.name === selectedCategory)?.icon}{' '}
              {categories.find(c => c.name === selectedCategory)?.displayName}
            </h2>
          )}

          <div className="comm-filters">
            <select value={sortBy} onChange={e => { setSortBy(e.target.value); fetchDiscussions(selectedCategory); }}>
              <option value="latest">Latest</option>
              <option value="popular">Most Viewed</option>
              <option value="most-liked">Most Liked</option>
            </select>
          </div>

          {loading ? (
            <div className="comm-loading">Loading discussions...</div>
          ) : discussions.length === 0 ? (
            <div className="comm-empty">
              <p>No discussions yet. Be the first to start one!</p>
            </div>
          ) : (
            <div className="comm-discussions-list">
              {discussions.map(disc => (
                <div 
                  key={disc.id} 
                  className="comm-discussion-item"
                  onClick={() => fetchDiscussion(disc.id)}
                >
                  <div className="comm-discussion-meta">
                    {disc.isPinned && <span className="comm-pinned">📌 Pinned</span>}
                  </div>
                  <h3>{disc.title}</h3>
                  <p>{disc.content.substring(0, 200)}{disc.content.length > 200 ? '...' : ''}</p>
                  <div className="comm-discussion-stats">
                    <span>👤 {disc.author.fullName}</span>
                    <span>👁 {disc.viewCount}</span>
                    <span>❤️ {disc.likeCount}</span>
                    <span>💬 {disc._count.replies}</span>
                    <span>{formatDate(disc.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Single Discussion View */}
      {view === 'discussion' && selectedDiscussion && (
        <div className="comm-discussion-detail">
          <button className="comm-breadcrumb" onClick={handleBackToDiscussions}>
            ← Back to Discussions
          </button>

          <div className="comm-discussion-header">
            <span className="comm-discussion-category">
              {selectedDiscussion.category.icon} {selectedDiscussion.category.displayName}
            </span>
            {editingDiscussionId === selectedDiscussion.id ? (
              <div className="comm-edit-form">
                <input
                  type="text"
                  value={editDiscTitle}
                  onChange={e => setEditDiscTitle(e.target.value)}
                  className="comm-edit-title"
                />
                <textarea
                  value={editDiscContent}
                  onChange={e => setEditDiscContent(e.target.value)}
                  rows={4}
                  className="comm-edit-content"
                />
                <div className="comm-edit-actions">
                  <button className="comm-save-btn" onClick={handleEditDiscussion}>Save</button>
                  <button className="comm-cancel-btn" onClick={() => setEditingDiscussionId(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <h2>{selectedDiscussion.title}</h2>
                <div className="comm-discussion-author">
                  <span>👤 {selectedDiscussion.author.fullName}</span>
                  <span>{formatDate(selectedDiscussion.createdAt)}</span>
                  {currentUserId === selectedDiscussion.author.id && (
                    <div className="comm-owner-actions">
                      <button
                        className="comm-edit-btn"
                        onClick={() => {
                          setEditingDiscussionId(selectedDiscussion.id);
                          setEditDiscTitle(selectedDiscussion.title);
                          setEditDiscContent(selectedDiscussion.content);
                        }}
                      >✏️ Edit</button>
                      <button
                        className="comm-delete-btn"
                        onClick={() => handleDeleteDiscussion(selectedDiscussion.id)}
                      >🗑️ Delete</button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {editingDiscussionId !== selectedDiscussion.id && (
            <div className="comm-discussion-content">
              <p>{selectedDiscussion.content}</p>
            </div>
          )}

          <div className="comm-discussion-actions">
            <button onClick={() => handleLike(selectedDiscussion.id)}>
              ❤️ {selectedDiscussion.likeCount}
            </button>
            <span>👁 {selectedDiscussion.viewCount} views</span>
          </div>

          <div className="comm-replies-section">
            <h3>Replies ({selectedDiscussion.replies.length})</h3>
            
            <form onSubmit={handleReply} className="comm-reply-form">
              {replyToId && (
                <div className="comm-reply-to-banner">
                  Replying to <strong>{replyToName}</strong>
                  <button type="button" onClick={() => { setReplyToId(null); setReplyToName(''); }}>✕</button>
                </div>
              )}
              <textarea
                value={replyContent}
                onChange={e => setReplyContent(e.target.value)}
                placeholder={replyToId ? `Reply to ${replyToName}...` : 'Share your thoughts...'}
                rows={3}
              />
              <button type="submit" disabled={submitting || !replyContent.trim()}>
                {submitting ? 'Posting...' : 'Post Reply'}
              </button>
            </form>

            <div className="comm-replies-list">
              {selectedDiscussion.replies.map(reply => (
                <div key={reply.id} className="comm-reply">
                  <div className="comm-reply-header">
                    <span className="comm-reply-author">👤 {reply.author.fullName}</span>
                    <span className="comm-reply-date">{formatDate(reply.createdAt)}</span>
                  </div>
                  {editingReplyId === reply.id ? (
                    <div className="comm-edit-form inline">
                      <textarea
                        value={editReplyContent}
                        onChange={e => setEditReplyContent(e.target.value)}
                        rows={3}
                      />
                      <div className="comm-edit-actions">
                        <button className="comm-save-btn" onClick={handleEditReply}>Save</button>
                        <button className="comm-cancel-btn" onClick={() => setEditingReplyId(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <p>{reply.content}</p>
                  )}
                  <div className="comm-reply-actions">
                    <button className="comm-reply-like" onClick={() => handleReplyLike(reply.id)}>
                      ❤️ {reply.likeCount}
                    </button>
                    <button className="comm-reply-reply" onClick={() => startReplyTo(reply.id, reply.author.fullName)}>
                      💬 Reply
                    </button>
                    {currentUserId === reply.author.id && editingReplyId !== reply.id && (
                      <>
                        <button className="comm-edit-btn sm" onClick={() => { setEditingReplyId(reply.id); setEditReplyContent(reply.content); }}>
                          ✏️
                        </button>
                        <button className="comm-delete-btn sm" onClick={() => handleDeleteReply(reply.id)}>
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                  
                  {reply.childReplies && reply.childReplies.length > 0 && (
                    <div className="comm-nested-replies">
                      {reply.childReplies.map(child => (
                        <div key={child.id} className="comm-reply nested">
                          <div className="comm-reply-header">
                            <span className="comm-reply-author">👤 {child.author.fullName}</span>
                            <span className="comm-reply-date">{formatDate(child.createdAt)}</span>
                          </div>
                          {editingReplyId === child.id ? (
                            <div className="comm-edit-form inline">
                              <textarea
                                value={editReplyContent}
                                onChange={e => setEditReplyContent(e.target.value)}
                                rows={2}
                              />
                              <div className="comm-edit-actions">
                                <button className="comm-save-btn" onClick={handleEditReply}>Save</button>
                                <button className="comm-cancel-btn" onClick={() => setEditingReplyId(null)}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <p>{child.content}</p>
                          )}
                          <div className="comm-reply-actions">
                            <button className="comm-reply-like" onClick={() => handleReplyLike(child.id)}>
                              ❤️ {child.likeCount}
                            </button>
                            <button className="comm-reply-reply" onClick={() => startReplyTo(reply.id, child.author.fullName)}>
                              💬 Reply
                            </button>
                            {currentUserId === child.author.id && editingReplyId !== child.id && (
                              <>
                                <button className="comm-edit-btn sm" onClick={() => { setEditingReplyId(child.id); setEditReplyContent(child.content); }}>
                                  ✏️
                                </button>
                                <button className="comm-delete-btn sm" onClick={() => handleDeleteReply(child.id)}>
                                  🗑️
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Community;
